import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '../hooks/useFormatting';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';
import { TimeframeData, PoliticianData, ChartDataPoint } from '../types';
import './PerformanceChart.css';

interface PerformanceChartProps {
  data: TimeframeData;
  selectedPolitician: PoliticianData | null;
  onSelectPolitician: (politician: PoliticianData | null) => void;
}

type CalloutDisplayMode = 'hover' | 'showAll';
type CalloutPosition = 'above' | 'below' | 'left' | 'right';

// Radial position constants for smart callout placement
const RADIAL_POSITIONS = [
  { angle: 90 },    // top (preferred)
  { angle: 60 },    // top-right 1
  { angle: 120 },   // top-left 1
  { angle: 45 },    // top-right 2
  { angle: 135 },   // top-left 2
  { angle: 30 },    // right high
  { angle: 150 },   // left high
  { angle: 0 },     // right
  { angle: 180 },   // left
  { angle: 330 },   // right low
  { angle: 210 },   // left low
  { angle: 270 },   // bottom (last resort)
];

const CALLOUT_DISTANCE = 80;  // Distance from dot center
const MIN_CALLOUT_GAP = 20;   // Minimum spacing between callouts

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  selectedPolitician,
  onSelectPolitician,
}) => {
  const { t, i18n } = useTranslation();
  const { formatDate, formatCurrency } = useFormatting();

  // Track which trade dot is being hovered
  const [hoveredTradeIndex, setHoveredTradeIndex] = useState<number | null>(null);
  // Toggle between hover and show all modes
  const [calloutDisplayMode] = useState<CalloutDisplayMode>('hover');
  // Methodology disclaimer expansion
  const [showMethodology, setShowMethodology] = useState(false);
  // Track if on mobile for responsive margins
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Chart container ref for actual dimensions
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState({ width: 800, height: 500 });

  // Update chart dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (chartContainerRef.current) {
        const { width, height } = chartContainerRef.current.getBoundingClientRect();
        setChartDimensions({ width, height });
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive chart margins
  const chartMargins = useMemo(() =>
    isMobile
      ? { top: 10, right: 10, left: 0, bottom: 10 }
      : { top: 20, right: 30, left: 20, bottom: 20 },
    [isMobile]
  );

  // Merge all timeseries data into chart format
  const chartData = useMemo(() => {
    const dateMap = new Map<string, ChartDataPoint>();

    // Add politician data
    Object.entries(data.politicians).forEach(([name, politician]) => {
      politician.data.forEach(point => {
        const dateKey = point.date.split('T')[0];
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { date: dateKey });
        }
        const entry = dateMap.get(dateKey)!;
        entry[name] = point.value;
      });
    });

    // Add benchmark data
    Object.entries(data.benchmarks).forEach(([name, benchmark]) => {
      benchmark.data.forEach(point => {
        const dateKey = point.date.split('T')[0];
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { date: dateKey });
        }
        const entry = dateMap.get(dateKey)!;
        entry[name] = point.value;
      });
    });

    return Array.from(dateMap.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
  }, [data]);

  const formatXAxis = useCallback((dateStr: string) => {
    return formatDate(dateStr, 'MMM yy');
  }, [formatDate]);

  const formatYAxis = useCallback((value: number) => {
    return formatCurrency(value, true);
  }, [formatCurrency]);

  const getLocalizedName = useCallback((politicianName: string) => {
    if (i18n.language.startsWith('zh')) {
      const politician = data.politicians[politicianName];
      if (politician) {
        const chineseName = t(`politician:names.${politician.id}`, { defaultValue: '' });
        return chineseName ? `${chineseName} (${politicianName})` : politicianName;
      }
    }
    return politicianName;
  }, [i18n.language, data.politicians, t]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);
    const topItems = sortedPayload.slice(0, 5);

    return (
      <div className="chart-tooltip">
        <div className="tooltip-date">
          {formatDate(label, 'MMM d, yyyy')}
        </div>
        <div className="tooltip-items">
          {topItems.map((item: any, idx: number) => (
            <div key={idx} className="tooltip-item">
              <span
                className="tooltip-dot"
                style={{ backgroundColor: item.color }}
              />
              <span className="tooltip-name">{getLocalizedName(item.name)}</span>
              <span className="tooltip-value">
                {formatCurrency(item.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleLineClick = useCallback(
    (name: string) => {
      const politician = data.politicians[name];
      if (politician) {
        onSelectPolitician(politician === selectedPolitician ? null : politician);
      }
    },
    [data.politicians, selectedPolitician, onSelectPolitician]
  );

  // Show only top performers (filtered by isTopPerformer flag)
  const visiblePoliticians = useMemo(() => {
    return Object.entries(data.politicians)
      .filter(([, politician]) => politician.isTopPerformer)
      .sort(([, a], [, b]) => b.currentValue - a.currentValue);
  }, [data.politicians]);

  // Calculate trade markers grouped by chart date
  const groupedTradeMarkers = useMemo(() => {
    if (!selectedPolitician?.trades || selectedPolitician.trades.length === 0) {
      return [];
    }

    // Create a map of date to portfolio value for this politician
    const dateToValue = new Map<string, number>();
    selectedPolitician.data.forEach(point => {
      const dateKey = point.date.split('T')[0];
      dateToValue.set(dateKey, point.value);
    });

    // Get sorted chart dates for matching
    const sortedChartDates = Array.from(dateToValue.keys()).sort();

    // Group trades by their matched chart date
    const tradesByDate = new Map<string, {
      chartDate: string;
      value: number;
      trades: Array<{
        originalDate: string;
        ticker: string;
        tradeType: string;
        simulatedAmount: number;
        tradeCount: number;
        portfolioPercentage: number;
      }>;
    }>();

    selectedPolitician.trades.forEach(trade => {
      const tradeDateKey = trade.pub_date;
      let matchedChartDate = tradeDateKey;
      let value = dateToValue.get(tradeDateKey);

      // Find the closest chart date <= trade date
      if (value === undefined) {
        for (let i = sortedChartDates.length - 1; i >= 0; i--) {
          if (sortedChartDates[i] <= tradeDateKey) {
            matchedChartDate = sortedChartDates[i];
            value = dateToValue.get(matchedChartDate);
            break;
          }
        }
      }

      // Skip trades that fall before the chart's date range
      if (value === undefined) {
        return;
      }

      if (!tradesByDate.has(matchedChartDate)) {
        tradesByDate.set(matchedChartDate, {
          chartDate: matchedChartDate,
          value: value,
          trades: [],
        });
      }

      tradesByDate.get(matchedChartDate)!.trades.push({
        originalDate: tradeDateKey,
        ticker: trade.ticker,
        tradeType: trade.trade_type,
        simulatedAmount: trade.simulated_amount,
        tradeCount: trade.trade_count,
        portfolioPercentage: trade.portfolio_percentage,
      });
    });

    // Net trades by ticker on the same date
    const nettedTradesByDate = Array.from(tradesByDate.values()).map(dateGroup => {
      // Group trades by ticker
      const tradesByTicker = new Map<string, {
        originalDate: string;
        ticker: string;
        netAmount: number;
        tradeCount: number;
        portfolioPercentage: number;
      }>();

      dateGroup.trades.forEach(trade => {
        const existing = tradesByTicker.get(trade.ticker);
        const isBuy = trade.tradeType.toLowerCase().includes('buy');
        const amount = isBuy ? trade.simulatedAmount : -trade.simulatedAmount;
        const percentage = isBuy ? trade.portfolioPercentage : -trade.portfolioPercentage;

        if (existing) {
          existing.netAmount += amount;
          existing.tradeCount += trade.tradeCount;
          existing.portfolioPercentage += percentage;
        } else {
          tradesByTicker.set(trade.ticker, {
            originalDate: trade.originalDate,
            ticker: trade.ticker,
            netAmount: amount,
            tradeCount: trade.tradeCount,
            portfolioPercentage: percentage,
          });
        }
      });

      // Convert back to trades array, filtering out zero-net trades
      const nettedTrades = Array.from(tradesByTicker.values())
        .filter(t => Math.abs(t.netAmount) > 0)
        .map(t => ({
          originalDate: t.originalDate,
          ticker: t.ticker,
          tradeType: t.netAmount > 0 ? 'BUY' : 'SELL',
          simulatedAmount: Math.abs(t.netAmount),
          tradeCount: t.tradeCount,
          portfolio_percentage: Math.abs(t.portfolioPercentage),
        }));

      return {
        ...dateGroup,
        trades: nettedTrades,
      };
    }).filter(dateGroup => dateGroup.trades.length > 0); // Remove dates with no trades after netting

    return nettedTradesByDate;
  }, [selectedPolitician]);

  // Format dollar amount for display
  const formatAmount = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  // Calculate responsive callout width based on chart width
  const calculateCalloutWidth = useCallback((chartWidth: number): number => {
    if (chartWidth < 600) return 140;   // Mobile
    if (chartWidth < 900) return 160;   // Tablet
    return 180;                          // Desktop
  }, []);

  // Calculate radial position for a callout
  const calculateRadialPosition = useCallback((
    dotX: number,
    dotY: number,
    angle: number,
    calloutWidth: number,
    calloutHeight: number,
    chartWidth: number,
    chartHeight: number
  ): { x: number; y: number; arrowX: number; arrowY: number } | null => {
    const radians = (angle * Math.PI) / 180;
    const distance = CALLOUT_DISTANCE;

    // Calculate callout center position
    const centerX = dotX + Math.cos(radians) * distance;
    const centerY = dotY - Math.sin(radians) * distance;

    // Convert to top-left corner
    const x = centerX - calloutWidth / 2;
    const y = centerY - calloutHeight / 2;

    // Check if callout fits within chart bounds
    if (x < 0 || x + calloutWidth > chartWidth) return null;
    if (y < 0 || y + calloutHeight > chartHeight) return null;

    return {
      x,
      y,
      arrowX: dotX,  // Arrow points to dot center
      arrowY: dotY,
    };
  }, []);

  // Collision detection with gap
  const checkAABBCollisionWithGap = useCallback((
    x1: number, y1: number, w1: number, h1: number,
    x2: number, y2: number, w2: number, h2: number,
    gap: number = 0
  ): boolean => {
    return !(
      x1 + w1 + gap < x2 ||
      x2 + w2 + gap < x1 ||
      y1 + h1 + gap < y2 ||
      y2 + h2 + gap < y1
    );
  }, []);

  // Calculate callout dimensions
  const getCalloutDimensions = (tradeCount: number) => {
    const lineHeight = 22;  // Increased from 18 for 2-line display (percentage + amount)
    const padding = 10;
    const width = calculateCalloutWidth(chartDimensions.width);
    const height = padding * 2 + tradeCount * lineHeight + 24;
    return { width, height, lineHeight, padding };
  };

  // Calculate positioned callouts with overlap avoidance using radial positioning
  const positionedCallouts = useMemo(() => {
    if (calloutDisplayMode !== 'showAll' || groupedTradeMarkers.length === 0) {
      return [];
    }

    // Use actual chart dimensions from ref
    const chartWidth = chartDimensions.width;
    const chartHeight = chartDimensions.height;
    const margin = chartMargins;
    const plotWidth = chartWidth - margin.left - margin.right;
    const plotHeight = chartHeight - margin.top - margin.bottom;

    const positioned: Array<{
      index: number;
      position: CalloutPosition;
      bounds: { x: number; y: number; width: number; height: number };
      arrowX?: number;
      arrowY?: number;
    }> = [];

    // Sort markers by x position (date) for left-to-right processing
    const sortedMarkers = [...groupedTradeMarkers]
      .map((group, originalIndex) => ({ group, originalIndex }))
      .sort((a, b) => a.group.chartDate.localeCompare(b.group.chartDate));

    const calloutWidth = calculateCalloutWidth(chartWidth);

    sortedMarkers.forEach(({ group, originalIndex }) => {
      const calloutHeight = 40 + group.trades.length * 20;  // Dynamic height

      // Calculate dot x,y position on chart
      const dateIndex = chartData.findIndex(d => d.date === group.chartDate);
      const xRatio = dateIndex / (chartData.length - 1);
      const cx = margin.left + xRatio * plotWidth;

      // For y, calculate based on value and data range
      const values = chartData.map(d => {
        const val = d[selectedPolitician?.name || ''];
        return typeof val === 'number' ? val : 0;
      }).filter(v => v > 0);
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      const yRatio = 1 - (group.value - minVal) / (maxVal - minVal);
      const cy = margin.top + yRatio * plotHeight;

      let bestPosition: CalloutPosition = 'above';
      let bestBounds: { x: number; y: number; width: number; height: number; arrowX?: number; arrowY?: number } = {
        x: 0,
        y: 0,
        width: calloutWidth,
        height: calloutHeight,
      };

      // Try each radial position in priority order
      for (const { angle } of RADIAL_POSITIONS) {
        const position = calculateRadialPosition(
          cx,
          cy,
          angle,
          calloutWidth,
          calloutHeight,
          chartWidth,
          chartHeight
        );

        if (!position) continue;  // Out of bounds

        // Check collision with all placed callouts (with gap)
        const hasCollision = positioned.some(placed =>
          checkAABBCollisionWithGap(
            position.x,
            position.y,
            calloutWidth,
            calloutHeight,
            placed.bounds.x,
            placed.bounds.y,
            placed.bounds.width,
            placed.bounds.height,
            MIN_CALLOUT_GAP
          )
        );

        if (!hasCollision) {
          bestPosition = 'above';  // Default label, actual position is radial
          bestBounds = {
            x: position.x,
            y: position.y,
            width: calloutWidth,
            height: calloutHeight,
          };
          break;  // Use first valid position
        }
      }

      // Fallback: stack vertically with offset if no valid position
      if (!bestBounds.x) {
        const stackCount = positioned.filter(
          c => Math.abs(c.bounds.x - cx) < 50
        ).length;

        bestBounds = {
          x: cx + 60,
          y: cy - 100 - (stackCount * 10),
          width: calloutWidth,
          height: calloutHeight,
        };
      }

      positioned.push({
        index: originalIndex,
        position: bestPosition,
        bounds: bestBounds,
      });
    });

    return positioned;
  }, [
    calloutDisplayMode,
    groupedTradeMarkers,
    chartData,
    selectedPolitician?.name,
    chartDimensions,
    chartMargins,
    calculateCalloutWidth,
    calculateRadialPosition,
    checkAABBCollisionWithGap,
  ]);

  // Custom shape for trade marker with callout (hover to reveal)
  const TradeCallout = (props: any) => {
    const { cx, cy, payload, index, isHovered, position = 'above', isShowAll = false, calloutBounds, renderMode = 'both' } = props;
    if (!payload || !payload.trades) return null;

    const trades = payload.trades as Array<{
      originalDate: string;
      ticker: string;
      tradeType: string;
      simulatedAmount: number;
      tradeCount: number;
      portfolio_percentage: number;
    }>;

    // Determine if mostly buys or sells for dot color
    const buyCount = trades.filter(t => t.tradeType.toLowerCase().includes('buy')).length;
    const dotColor = buyCount > trades.length / 2 ? '#10b981' : '#ef4444';

    // Calculate callout dimensions based on content
    const { width: calloutWidth, height: calloutHeight, lineHeight, padding } = getCalloutDimensions(trades.length);

    // Calculate callout position based on direction
    let calloutX: number, calloutY: number;
    let arrowPoints: string;
    let coverLineProps: { x1: number; y1: number; x2: number; y2: number };
    let arrowPath: string | null = null;

    // Use custom bounds from radial positioning if in showAll mode
    if (isShowAll && calloutBounds) {
      calloutX = calloutBounds.x;
      calloutY = calloutBounds.y;

      // Calculate arrow path from callout edge to actual dot center (cx, cy from Recharts)
      const calloutCenterX = calloutX + calloutWidth / 2;
      const calloutCenterY = calloutY + calloutHeight / 2;

      // Find closest edge point on callout rectangle
      let edgeX = calloutCenterX;
      let edgeY = calloutCenterY;

      // Determine which edge is closest to the dot
      const dx = cx - calloutCenterX;
      const dy = cy - calloutCenterY;
      const slope = Math.abs(dy / (dx || 1));
      const rectSlope = calloutHeight / calloutWidth;

      if (slope < rectSlope) {
        // Left or right edge
        edgeX = dx > 0 ? calloutX + calloutWidth : calloutX;
        edgeY = calloutCenterY;
      } else {
        // Top or bottom edge
        edgeX = calloutCenterX;
        edgeY = dy > 0 ? calloutY + calloutHeight : calloutY;
      }

      arrowPath = `M ${edgeX} ${edgeY} L ${cx} ${cy}`;

      // Placeholder values for compatibility (won't be used)
      arrowPoints = '';
      coverLineProps = { x1: 0, y1: 0, x2: 0, y2: 0 };
    } else {
      // Use default positioning for hover mode
      switch (position) {
        case 'below':
          calloutX = cx - calloutWidth / 2;
          calloutY = cy + 15;
          arrowPoints = `${cx - 6},${cy + 8} ${cx + 6},${cy + 8} ${cx},${cy + 2}`;
          coverLineProps = { x1: cx - 5, y1: cy + 8, x2: cx + 5, y2: cy + 8 };
          break;
        case 'left':
          calloutX = cx - calloutWidth - 15;
          calloutY = cy - calloutHeight / 2;
          arrowPoints = `${cx - 8},${cy - 6} ${cx - 8},${cy + 6} ${cx - 2},${cy}`;
          coverLineProps = { x1: cx - 8, y1: cy - 5, x2: cx - 8, y2: cy + 5 };
          break;
        case 'right':
          calloutX = cx + 15;
          calloutY = cy - calloutHeight / 2;
          arrowPoints = `${cx + 8},${cy - 6} ${cx + 8},${cy + 6} ${cx + 2},${cy}`;
          coverLineProps = { x1: cx + 8, y1: cy - 5, x2: cx + 8, y2: cy + 5 };
          break;
        case 'above':
        default:
          calloutX = cx - calloutWidth / 2;
          calloutY = cy - calloutHeight - 15;
          arrowPoints = `${cx - 6},${cy - 8} ${cx + 6},${cy - 8} ${cx},${cy - 2}`;
          coverLineProps = { x1: cx - 5, y1: cy - 8, x2: cx + 5, y2: cy - 8 };
          break;
      }
    }

    const showCallout = isHovered || isShowAll;

    return (
      <g style={{ zIndex: 1000 }}>
        {/* Dots - only render in 'dots' or 'both' mode */}
        {(renderMode === 'dots' || renderMode === 'both') && (
          <>
            <circle
              cx={cx}
              cy={cy}
              r={isHovered ? 8 : 6}
              fill={dotColor}
              stroke="white"
              strokeWidth={2}
              style={{ cursor: 'pointer', transition: 'r 0.15s ease' }}
              onMouseEnter={() => setHoveredTradeIndex(index)}
              onMouseLeave={() => setHoveredTradeIndex(null)}
            />
            {/* Invisible larger hit area for easier hovering */}
            <circle
              cx={cx}
              cy={cy}
              r={12}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredTradeIndex(index)}
              onMouseLeave={() => setHoveredTradeIndex(null)}
            />
          </>
        )}

        {/* Callout - only render in 'callouts' or 'both' mode */}
        {(renderMode === 'callouts' || renderMode === 'both') && showCallout && (
          <g style={{ zIndex: 1001 }}>
            {/* Callout box */}
            <rect
              x={calloutX}
              y={calloutY}
              width={calloutWidth}
              height={calloutHeight}
              rx={6}
              fill="white"
              stroke="#e5e7eb"
              strokeWidth={1}
              filter="url(#callout-shadow)"
            />
            {/* Arrow pointing to dot */}
            {arrowPath ? (
              // Use path-based arrow for radial positioning
              <path
                d={arrowPath}
                stroke="#6b7280"
                strokeWidth="1.5"
                fill="none"
              />
            ) : (
              <>
                {/* Use polygon arrow for default positioning */}
                <polygon
                  points={arrowPoints}
                  fill="white"
                  stroke="#e5e7eb"
                  strokeWidth={1}
                />
                {/* Cover arrow edge stroke */}
                <line
                  {...coverLineProps}
                  stroke="white"
                  strokeWidth={2}
                />
              </>
            )}

            {/* Date header */}
            <text
              x={calloutX + calloutWidth / 2}
              y={calloutY + padding + 12}
              textAnchor="middle"
              fontSize={10}
              fontWeight={600}
              fill="#6b7280"
            >
              {formatDate(trades[0].originalDate, 'MMM d, yyyy')}
            </text>

            {/* Trade list */}
            {trades.map((trade, idx) => {
              const isBuy = trade.tradeType.toLowerCase().includes('buy');
              const tradeColor = isBuy ? '#10b981' : '#ef4444';
              return (
                <g key={idx}>
                  {/* BUY/SELL label */}
                  <text
                    x={calloutX + 8}
                    y={calloutY + padding + 30 + idx * lineHeight}
                    fontSize={8}
                    fontWeight={700}
                    fill={tradeColor}
                  >
                    {isBuy ? t('chart:buy') : t('chart:sell')}
                  </text>
                  {/* Ticker */}
                  <text
                    x={calloutX + 38}
                    y={calloutY + padding + 30 + idx * lineHeight}
                    fontSize={10}
                    fontWeight={600}
                    fill="#1f2937"
                  >
                    {trade.ticker.split(':')[0]}
                  </text>
                  {/* Trade percentage */}
                  <text
                    x={calloutX + calloutWidth - 8}
                    y={calloutY + padding + 30 + idx * lineHeight}
                    textAnchor="end"
                    fontSize={8}
                    fontWeight={600}
                    fill="#d97706"
                  >
                    {trade.portfolio_percentage?.toFixed(1)}%
                  </text>
                  {/* Trade dollar amount */}
                  <text
                    x={calloutX + calloutWidth - 8}
                    y={calloutY + padding + 30 + idx * lineHeight + 11}
                    textAnchor="end"
                    fontSize={8}
                    fontWeight={500}
                    fill="#6b7280"
                  >
                    {formatAmount(trade.simulatedAmount)}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </g>
    );
  };

  return (
    <div ref={chartContainerRef} className={`performance-chart ${hoveredTradeIndex !== null ? 'trade-callout-active' : ''}`}>
      <div className="chart-header">
        <h2 className="chart-title">{t('chart:title')}</h2>
        <div className="chart-controls">
          <div className="chart-legend">
            <div className="legend-item">
              <span className="legend-line dashed" style={{ borderColor: '#9ca3af' }} />
              <span>{t('chart:spy')}</span>
            </div>
            <div className="legend-item">
              <span className="legend-line dotted" style={{ borderColor: '#6b7280' }} />
              <span>{t('chart:qqq')}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={chartMargins}
          onClick={(data: any) => {
            // Only deselect if clicking empty space (not on a line or dot)
            if (selectedPolitician && !data?.activePayload) {
              onSelectPolitician(null);
            }
          }}
        >
          <defs>
            {/* Glow filters for top performers */}
            <filter id="glow-blue" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="glow-red" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            {/* Shadow for callout boxes */}
            <filter id="callout-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
            </filter>
          </defs>

          <XAxis
            dataKey="date"
            tickFormatter={formatXAxis}
            stroke="#9ca3af"
            tick={{ fill: '#6b7280', fontSize: 11 }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={{ stroke: '#e5e7eb' }}
            interval="preserveStartEnd"
            minTickGap={60}
          />
          <YAxis
            tickFormatter={formatYAxis}
            stroke="#9ca3af"
            tick={{ fill: '#6b7280', fontSize: 11 }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={{ stroke: '#e5e7eb' }}
            domain={['auto', 'auto']}
          />
          {/* Only show tooltip when no politician is selected */}
          {!selectedPolitician && <Tooltip content={<CustomTooltip />} />}

          {/* Reference line at $10,000 */}
          <ReferenceLine
            y={10000}
            stroke="#d1d5db"
            strokeDasharray="4 4"
            label={{
              value: 'Initial $10K',
              fill: '#9ca3af',
              fontSize: 10,
              position: 'right',
            }}
          />

          {/* SPY benchmark */}
          <Line
            type="monotone"
            dataKey="SPY"
            stroke="#9ca3af"
            strokeWidth={2}
            strokeDasharray="8 4"
            dot={false}
            activeDot={selectedPolitician ? false : { r: 4, fill: '#9ca3af' }}
          />

          {/* QQQ benchmark */}
          <Line
            type="monotone"
            dataKey="QQQ"
            stroke="#6b7280"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            activeDot={selectedPolitician ? false : { r: 4, fill: '#6b7280' }}
          />

          {/* Visible politicians: top performers */}
          {visiblePoliticians
            .filter(([, pol]) => pol.data.length > 0)
            .map(([name, politician]) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={politician.color}
                strokeWidth={selectedPolitician?.name === name ? 3 : 2}
                strokeDasharray={undefined}
                dot={false}
                activeDot={selectedPolitician ? false : { r: 5, fill: politician.color }}
                opacity={
                  selectedPolitician && selectedPolitician.name !== name ? 0.4 : 1
                }
                style={{ cursor: 'pointer' }}
                onClick={() => handleLineClick(name)}
              />
            ))}

          {/* Trade dots - render first so callouts appear on top */}
          {groupedTradeMarkers.map((group, idx) => {
            const positioned = positionedCallouts.find(p => p.index === idx);
            const position = positioned?.position || 'above';
            const isShowAll = calloutDisplayMode === 'showAll';

            return (
              <ReferenceDot
                key={`trade-dot-${idx}`}
                x={group.chartDate}
                y={group.value}
                r={0}
                shape={(props: any) => (
                  <TradeCallout
                    {...props}
                    payload={group}
                    index={idx}
                    isHovered={hoveredTradeIndex === idx}
                    position={position}
                    isShowAll={isShowAll}
                    calloutBounds={positioned?.bounds}
                    renderMode="dots"
                  />
                )}
              />
            );
          })}

          {/* Trade callouts - render last so they appear on top of all dots */}
          {groupedTradeMarkers.map((group, idx) => {
            const positioned = positionedCallouts.find(p => p.index === idx);
            const position = positioned?.position || 'above';
            const isShowAll = calloutDisplayMode === 'showAll';

            return (
              <ReferenceDot
                key={`trade-callout-${idx}`}
                x={group.chartDate}
                y={group.value}
                r={0}
                shape={(props: any) => (
                  <TradeCallout
                    {...props}
                    payload={group}
                    index={idx}
                    isHovered={hoveredTradeIndex === idx}
                    position={position}
                    isShowAll={isShowAll}
                    calloutBounds={positioned?.bounds}
                    renderMode="callouts"
                  />
                )}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
      </div>

      <div className="chart-annotations">
        {selectedPolitician && (
          <div
            className="annotation-badge"
            style={{ borderColor: selectedPolitician.color }}
          >
            <span className="annotation-name">{selectedPolitician.name}</span>
            <span className="annotation-value" style={{ color: selectedPolitician.currentValue >= 10000 ? '#10b981' : '#ef4444' }}>
              ${selectedPolitician.currentValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
        )}
      </div>

      <div className="chart-disclaimer">
        <p className="disclaimer-text">
          {t('chart:disclaimer')}
          <button
            className="disclaimer-toggle"
            onClick={() => setShowMethodology(!showMethodology)}
          >
            {showMethodology ? t('chart:less') : t('chart:learnMore')}
          </button>
        </p>
        {showMethodology && (
          <p className="disclaimer-detail">
            {t('chart:methodologyDetail')}
          </p>
        )}
      </div>
    </div>
  );
};

export default PerformanceChart;
